const HEX_PATTERN = /^#([0-9a-f]{6})$/i;

const FALLBACK_ACCENT = "#D95032";

export function isValidHexColor(value: string): boolean {
  return HEX_PATTERN.test(value.trim());
}

/** Tenant runtime colors are operator-chosen and unconstrained; anything
 * malformed falls back to the default brand accent rather than breaking layout. */
export function sanitizeTenantColor(value: string | undefined | null): string {
  if (value && isValidHexColor(value)) return value.trim();
  return FALLBACK_ACCENT;
}

function relativeLuminance(hex: string): number {
  const match = HEX_PATTERN.exec(hex);
  if (!match) return 0;
  const channels = [0, 2, 4].map((offset) => {
    const value = parseInt(match[1]!.slice(offset, offset + 2), 16) / 255;
    return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * channels[0]! + 0.7152 * channels[1]! + 0.0722 * channels[2]!;
}

/** WCAG contrast ratio of two colors, 1 (identical) to 21 (black on white). */
export function contrastRatio(hexA: string, hexB: string): number {
  const a = relativeLuminance(hexA);
  const b = relativeLuminance(hexB);
  const lighter = Math.max(a, b);
  const darker = Math.min(a, b);
  return (lighter + 0.05) / (darker + 0.05);
}

/** Picks a preferred ink or white when it clears AA, then falls back to pure
 * black or white for mid-tone tenant colors where neither preferred token does. */
export function readableTextColor(backgroundHex: string, inkHex: string, whiteHex: string): string {
  const inkContrast = contrastRatio(backgroundHex, inkHex);
  const whiteContrast = contrastRatio(backgroundHex, whiteHex);
  const preferred = whiteContrast >= inkContrast ? whiteHex : inkHex;
  if (Math.max(inkContrast, whiteContrast) >= 4.5) return preferred;

  return contrastRatio(backgroundHex, "#FFFFFF") >= contrastRatio(backgroundHex, "#000000")
    ? "#FFFFFF"
    : "#000000";
}
